import {useEffect, useState} from "react";
import {createPortal} from "react-dom";
import {getCaptcha, submitProductReview, type FieldRule} from "@zaaaz/shared";
import {DsButton, DsInput} from "@zaaaz/ui";
import {useFormValidation} from "@/lib/useFormValidation";
import {FormField} from "./FormField";
import {useI18n} from "./I18nProvider";

type ReviewField = "nume" | "email" | "mesaj" | "cod";

const reviewRules: FieldRule<ReviewField>[] = [
  {field: "nume", required: true},
  {field: "email", type: "email", required: true},
  {field: "mesaj", required: true},
  {field: "cod", required: true}
];

type Props = {
  open: boolean;
  onClose: () => void;
  productId: number;
  defaultNume?: string;
  defaultEmail?: string;
  onSubmitted?: () => void;
};

export function PdpReviewModal({open, onClose, productId, defaultNume = "", defaultEmail = "", onSubmitted}: Props) {
  const {t} = useI18n();
  const [mounted, setMounted] = useState(false);
  const [form, setForm] = useState({nume: "", email: "", telefon: "", mesaj: "", stars: 5, cod: ""});
  const [captcha, setCaptcha] = useState({nr1: 0, nr2: 0});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const {errors, clearError, validate, clearErrors} = useFormValidation<ReviewField>();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if(!open) return;
    setForm((f) => ({
      ...f,
      nume: defaultNume || f.nume,
      email: defaultEmail || f.email,
      mesaj: "",
      cod: "",
      stars: 5
    }));
    setError("");
    clearErrors();
    getCaptcha().then(setCaptcha).catch(() => {});
  }, [open, defaultNume, defaultEmail, clearErrors]);

  useEffect(() => {
    if(!open) return;
    function onKey(e: KeyboardEvent) {
      if(e.key === "Escape" && !submitting) onClose();
    }
    document.addEventListener("keydown", onKey);
    document.body.classList.add("noscroll");
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.classList.remove("noscroll");
      document.body.style.overflow = prev;
    };
  }, [open, onClose, submitting]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if(!validate(reviewRules, form, t)) return;
    setSubmitting(true);
    try {
      await submitProductReview(productId, {...form, cod: parseInt(form.cod, 10)});
      onSubmitted?.();
      onClose();
    } catch {
      setError(t("The result is incorrect."));
      getCaptcha().then(setCaptcha).catch(() => {});
      setForm((f) => ({...f, cod: ""}));
    } finally {
      setSubmitting(false);
    }
  }

  if(!mounted || !open) return null;

  return createPortal(
    <div className="pvPdpReviewModal" role="dialog" aria-modal="true" aria-labelledby="pvPdpReviewModalTitle">
      <button type="button" className="pvPdpReviewModal__backdrop" aria-label={t("Close")} onClick={onClose} disabled={submitting} />
      <div className="pvPdpReviewModal__panel">
        <header className="pvPdpReviewModal__head">
          <span className="pvPdpReviewModal__headIcon" aria-hidden="true">
            <i className="fa fa-commenting-o" />
          </span>
          <h2 id="pvPdpReviewModalTitle" className="pvPdpReviewModal__title">{t("Your review")}</h2>
          <button type="button" className="pvPdpReviewModal__close" onClick={onClose} aria-label={t("Close")} disabled={submitting}>
            <i className="fa fa-times" aria-hidden="true" />
          </button>
        </header>
        <form className="pvPdpReviewModal__body" onSubmit={onSubmit} noValidate>
          <DsInput
            name="nume"
            value={form.nume}
            onChange={(e) => { setForm({...form, nume: e.target.value}); clearError("nume"); }}
            placeholder={t("Name")}
            error={errors.nume}
            aria-required="true"
          />
          <DsInput
            type="email"
            name="email"
            value={form.email}
            onChange={(e) => { setForm({...form, email: e.target.value}); clearError("email"); }}
            placeholder={t("E-mail address")}
            error={errors.email}
            aria-required="true"
          />
          <DsInput
            type="tel"
            name="telefon"
            value={form.telefon}
            onChange={(e) => setForm({...form, telefon: e.target.value})}
            placeholder={t("Phone (optional)")}
          />
          <FormField error={errors.mesaj}>
            <textarea
              name="mesaj"
              value={form.mesaj}
              onChange={(e) => { setForm({...form, mesaj: e.target.value}); clearError("mesaj"); }}
              placeholder={t("Message")}
              className="pvPdpReviewModal__textarea"
              rows={4}
              aria-required="true"
            />
          </FormField>
          <div className="pvPdpReviewModal__stars">
            <span className="pvPdpReviewModal__starsLabel">{t("Stars")}</span>
            <div className="pvPdpReviewModal__starsRow" role="radiogroup" aria-label={t("Stars")}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className={`pvPdpReviewModal__star${star <= form.stars ? " isActive" : ""}`}
                  onClick={() => setForm({...form, stars: star})}
                  aria-label={`${star}`}
                  aria-pressed={star <= form.stars}
                >
                  <i className={`fa${star <= form.stars ? " fa-star" : " fa-star-o"}`} aria-hidden="true" />
                </button>
              ))}
            </div>
          </div>
          <div className="pvPdpReviewModal__captcha">
            <p className="pvPdpReviewModal__captchaLabel">
              {t("The sum of")} <strong>{captcha.nr1} + {captcha.nr2}</strong>
            </p>
            <DsInput
              name="cod"
              value={form.cod}
              onChange={(e) => { setForm({...form, cod: e.target.value}); clearError("cod"); }}
              placeholder={t("The result is...")}
              error={errors.cod}
              inputMode="numeric"
              aria-required="true"
            />
          </div>
          {error ? <p className="pvPdpReviewModal__error" role="alert">{error}</p> : null}
          <div className="pvPdpReviewModal__actions">
            <DsButton type="button" variant="outline" onClick={onClose} disabled={submitting}>
              {t("Close")}
            </DsButton>
            <DsButton type="submit" variant="primary" disabled={submitting}>
              {submitting ? "..." : t("Send review")}
            </DsButton>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
